import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSales } from "@/contexts/SalesContext";

/**
 * Admin-only page to edit the global sales contact (WhatsApp + e-mail + display
 * phone). Updates flow through PUT /api/admin/settings/sales and trigger a
 * SalesContext refresh so all CTAs across the app pick up the new values
 * immediately — without requiring a deploy.
 */
export default function AdminSettings() {
  const sales = useSales();
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [busy, setBusy] = useState(false);

  // Hydrate the form from the same source the rest of the app reads.
  useEffect(() => {
    setWhatsapp(sales.whatsapp || "");
    setEmail(sales.email || "");
    setPhoneDisplay(sales.phoneDisplay || "");
  }, [sales.whatsapp, sales.email, sales.phoneDisplay]);

  const save = async () => {
    if (!whatsapp.trim() || !email.trim() || !phoneDisplay.trim()) {
      toast.error("Preencha todos os campos antes de salvar.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/settings/sales", {
        whatsapp: whatsapp.trim(),
        email: email.trim(),
        phone_display: phoneDisplay.trim(),
      });
      toast.success("Configurações atualizadas. Todas as páginas já refletem o novo contato.");
      sales.refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const previewWa = `https://wa.me/${whatsapp.replace(/\D/g, "")}`;
  const previewMail = `mailto:${email}`;

  return (
    <div className="p-8 lg:p-12 max-w-3xl mx-auto" data-testid="admin-settings-page">
      <div className="mb-8">
        <div className="overline mb-2">Administração</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Configurações comerciais</h1>
        <p className="text-slate-600 mt-2">
          (landing page, paywall do AEP, painel de leads). Edições são aplicadas <strong>em tempo real</strong> — sem necessidade de novo deploy.
        </p>
      </div>

      <div className="card space-y-6">
        <div>
          <label className="label" htmlFor="whatsapp-input">WhatsApp (DDI + DDD + número)</label>
          <input
            id="whatsapp-input"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="5534998282258"
            className="input font-mono"
            data-testid="settings-whatsapp-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Apenas dígitos, começando pelo código do país (55 = Brasil). Caracteres extras serão removidos no salvamento.
          </p>
          {whatsapp && (
            <a href={previewWa} target="_blank" rel="noreferrer"
               className="text-xs text-emerald-900 hover:underline mt-2 inline-block font-mono"
               data-testid="settings-whatsapp-preview">
              <i className="fa-brands fa-whatsapp mr-1"></i> Testar: {previewWa}
            </a>
          )}
        </div>

        <div>
          <label className="label" htmlFor="email-input">E-mail comercial</label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="comercial@suaempresa.com.br"
            className="input"
            data-testid="settings-email-input"
          />
          {email && (
            <a href={previewMail}
               className="text-xs text-emerald-900 hover:underline mt-2 inline-block font-mono"
               data-testid="settings-email-preview">
              <i className="fa-solid fa-envelope mr-1"></i> Testar: {previewMail}
            </a>
          )}
        </div>

        <div>
          <label className="label" htmlFor="phone-display-input">Telefone formatado para exibição</label>
          <input
            id="phone-display-input"
            value={phoneDisplay}
            onChange={(e) => setPhoneDisplay(e.target.value)}
            placeholder="(34) 99828-2258"
            className="input"
            data-testid="settings-phone-display-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Exibido em alguns lugares da interface para humanos (não usado em links).
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="settings-save-button">
            {busy ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-floppy-disk"></i>}
            Salvar configurações
          </button>
        </div>
      </div>

      <div className="card mt-6 border-emerald-200 bg-emerald-50">
        <div className="overline text-emerald-900 mb-2">Por que isso é importante?</div>
        <p className="text-sm text-emerald-900/90 leading-relaxed">
          Os botões &ldquo;Falar com comercial&rdquo; da landing pública, o paywall do plano demonstração e os atalhos do painel de leads usam estes dados.
          Mantendo-os corretos, leads quentes chegam imediatamente no canal certo — sem ligação ou e-mail perdido.
        </p>
      </div>
    </div>
  );
}
